import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  CalendarDays,
  Mail,
  Network,
  Pencil,
  Send,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';

import {
  deactivateEmployee,
  getEmployee,
  listDepartments,
  listEmployees,
  updateEmployee,
  type EmployeeDetail,
  type UpdateEmployeeRequest,
} from '@/api/employees.api';
import { createInvitation, listInvitations, resendInvitation, type Invitation } from '@/api/invitations.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Per-employee profile page. Phase 3 Day 2.
 *
 * Two audiences:
 *   - Anyone in the org (within their RBAC visibility) can view a
 *     profile. Server returns 404 if the caller can't see this row.
 *   - visionary | integrator can edit + soft-delete.
 *
 * Design choices:
 *   - The detail query reuses `getEmployee` so React-Query dedupes
 *     against any prefetch or list-row-hover prefetch we add later.
 *   - Edit + deactivate are gated client-side AND server-side (the UI
 *     hides the buttons for non-admins; the server still rejects the
 *     mutation if someone calls the API directly).
 *   - Email is read-only — see the api comment.
 */

export function EmployeeProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state: authState } = useAuth();

  // The literal `id` may be undefined for a moment during a router
  // transition. Capture it (or empty) so the hooks below are called
  // in a stable order on every render — React's hooks rule. The early
  // redirect happens AFTER all hooks have run.
  const employeeId = id ?? '';

  const employeeQuery = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployee(employeeId),
    enabled: employeeId !== '',
  });
  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
  });
  const peerCandidatesQuery = useQuery({
    // Used to populate the manager picker in the edit modal. We only
    // need active employees in the same org, sorted by name.
    queryKey: ['employees', { status: 'active' as const, limit: 500 }],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });

  const canManage = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return ['visionary', 'integrator'].includes(authState.me.membership.role);
  }, [authState]);

  const [editOpen, setEditOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const { show } = useToast();

  const deactivateMutation = useMutation({
    mutationFn: () => deactivateEmployee(employeeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      setDeactivateOpen(false);
      void navigate('/employees');
    },
  });

  // Invite status for this person. The list endpoint is admin-only —
  // non-admins never fetch and never see the chip/buttons.
  const invitationsQuery = useQuery({
    queryKey: ['invitations'],
    queryFn: listInvitations,
    enabled: canManage,
  });

  // Latest invitation for this email (server list is createdAt-desc).
  const latestInvite: Invitation | null = useMemo(() => {
    const email = employeeQuery.data?.email.toLowerCase();
    if (!email) return null;
    return invitationsQuery.data?.find((inv) => inv.email === email) ?? null;
  }, [invitationsQuery.data, employeeQuery.data]);

  const sendInviteMutation = useMutation({
    mutationFn: () => {
      // Pending or expired → rotate + re-send the existing invitation;
      // anything else (none / revoked) → create a fresh one.
      if (latestInvite && (latestInvite.status === 'pending' || latestInvite.status === 'expired')) {
        return resendInvitation(latestInvite.id);
      }
      const employee = employeeQuery.data;
      if (!employee) return Promise.reject(new Error('Employee not loaded'));
      return createInvitation({
        email: employee.email,
        departmentId: employee.departmentId ?? undefined,
        employeeId: employee.id,
      });
    },
    onSuccess: (result) => {
      show(`Invitation sent to ${result.invitation.email}`, { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: (err) => {
      show(describeInviteActionError(err), { variant: 'danger' });
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  // After all hooks: bounce a missing :id back to the directory.
  if (employeeId === '') return <Navigate to="/employees" replace />;

  if (employeeQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (employeeQuery.error || !employeeQuery.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Link
          to="/employees"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to directory
        </Link>
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Employee not available</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {employeeQuery.error instanceof Error
                ? employeeQuery.error.message
                : 'This person does not exist or you do not have access to view them.'}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const employee = employeeQuery.data;
  const initials = `${employee.firstName.charAt(0)}${employee.lastName.charAt(0)}`.toUpperCase() || '·';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/employees"
        className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
      >
        <ArrowLeft className="size-4" />
        Back to directory
      </Link>

      {/* Header */}
      <Card className="space-y-4 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="grid size-16 shrink-0 place-items-center rounded-kw-full bg-kw-cerulean text-[20px] font-medium text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h1 className="typo-h1">
                {employee.firstName} {employee.lastName}
              </h1>
              {!employee.isActive ? <Badge variant="neutral">Inactive</Badge> : null}
            </div>
            {employee.jobTitle ? <p className="mt-1 text-[14px] text-kw-shark">{employee.jobTitle}</p> : null}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-kw-muted">
              <a
                href={`mailto:${employee.email}`}
                className="inline-flex items-center gap-1 hover:text-kw-shark hover:underline"
              >
                <Mail className="size-3.5" />
                {employee.email}
              </a>
              {employee.department ? (
                <span className="inline-flex items-center gap-1">
                  <span className="size-2 rounded-full bg-kw-cerulean" aria-hidden />
                  {employee.department.name}
                </span>
              ) : (
                <span className="text-kw-ghost">No department</span>
              )}
              {employee.hireDate ? (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="size-3.5" />
                  Started {new Date(employee.hireDate).toLocaleDateString()}
                </span>
              ) : null}
              {employee.membershipId ? (
                <span className="inline-flex items-center gap-1 text-kw-tag-text">
                  <UserCheck className="size-3.5" />
                  Portal account active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-kw-ghost">
                  Directory only — no portal account yet
                </span>
              )}
              {/* Invite status chip — admin-only (the invitations list
                  endpoint is RBAC'd), and only meaningful while there's
                  no linked portal account. */}
              {canManage && !employee.membershipId ? (
                latestInvite?.status === 'pending' ? (
                  <Badge variant="info">Invite pending</Badge>
                ) : latestInvite?.status === 'expired' ? (
                  <Badge variant="warning">Invite expired</Badge>
                ) : latestInvite?.status === 'accepted' ? (
                  <Badge variant="success">Invite accepted</Badge>
                ) : (
                  <Badge variant="neutral">Not invited</Badge>
                )
              ) : null}
            </div>
          </div>
          {canManage ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              {!employee.membershipId ? (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={sendInviteMutation.isPending}
                  onClick={() => {
                    sendInviteMutation.mutate();
                  }}
                >
                  <Send className="size-4" />
                  {latestInvite?.status === 'pending' ? 'Resend invite' : 'Send invite'}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setEditOpen(true);
                }}
              >
                <Pencil className="size-4" />
                Edit
              </Button>
              {employee.isActive ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDeactivateOpen(true);
                  }}
                >
                  <Trash2 className="size-4" />
                  Deactivate
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {/* Reporting context */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="space-y-2 p-4">
          <h2 className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
            <Briefcase className="size-3.5" />
            Reports to
          </h2>
          {employee.manager ? (
            <Link
              to={`/employees/${employee.manager.id}`}
              className="block rounded-kw-md p-2 text-[14px] text-kw-shark hover:bg-kw-bg"
            >
              {employee.manager.firstName} {employee.manager.lastName}
            </Link>
          ) : (
            <p className="text-[13px] text-kw-ghost">No manager set.</p>
          )}
        </Card>

        <Card className="space-y-2 p-4">
          <h2 className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
            <Users className="size-3.5" />
            Direct reports <span className="font-normal text-kw-ghost">({employee.reports.length})</span>
          </h2>
          {employee.reports.length === 0 ? (
            <p className="text-[13px] text-kw-ghost">No direct reports.</p>
          ) : (
            <ul className="divide-y divide-kw-border">
              {employee.reports.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/employees/${r.id}`}
                    className="flex items-center justify-between rounded-kw-md p-2 text-[13px] text-kw-shark hover:bg-kw-bg"
                  >
                    <span>
                      {r.firstName} {r.lastName}
                    </span>
                    {r.jobTitle ? <span className="text-[12px] text-kw-muted">{r.jobTitle}</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* Cross-link to the chart for context. */}
      <Link
        to="/accountability"
        className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
      >
        <Network className="size-4" />
        See in accountability chart
      </Link>

      {canManage ? (
        <EditEmployeeModal
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
          }}
          employee={employee}
          departments={departmentsQuery.data ?? []}
          managerCandidates={peerCandidatesQuery.data?.data.filter((p) => p.id !== employee.id) ?? []}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['employees'] });
            void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
          }}
        />
      ) : null}

      {canManage ? (
        <Modal
          open={deactivateOpen}
          onClose={() => {
            setDeactivateOpen(false);
          }}
          title="Deactivate employee"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              Mark{' '}
              <strong>
                {employee.firstName} {employee.lastName}
              </strong>{' '}
              as inactive? They&apos;ll stay in the directory for historical context but won&apos;t appear on
              the active accountability chart unless someone toggles &quot;Show inactive&quot; on the
              directory.
            </p>
            <p className="text-[12px] text-kw-muted">
              You can reactivate them by editing the profile later — no data is destroyed.
            </p>
            {deactivateMutation.error ? (
              <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
                <p className="text-[12px] text-kw-danger-text">
                  {deactivateMutation.error instanceof Error
                    ? deactivateMutation.error.message
                    : 'Deactivate failed'}
                </p>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDeactivateOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                isLoading={deactivateMutation.isPending}
                onClick={() => {
                  deactivateMutation.mutate();
                }}
              >
                Deactivate
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Edit modal
// ─────────────────────────────────────────────────────────────────────────

interface DepartmentOption {
  id: string;
  name: string;
}
interface ManagerOption {
  id: string;
  firstName: string;
  lastName: string;
}

function EditEmployeeModal({
  open,
  onClose,
  employee,
  departments,
  managerCandidates,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  employee: EmployeeDetail;
  departments: DepartmentOption[];
  managerCandidates: ManagerOption[];
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(employee.firstName);
  const [lastName, setLastName] = useState(employee.lastName);
  const [jobTitle, setJobTitle] = useState(employee.jobTitle ?? '');
  const [departmentId, setDepartmentId] = useState(employee.departmentId ?? '');
  const [managerEmployeeId, setManagerEmployeeId] = useState(employee.managerEmployeeId ?? '');
  const [hireDate, setHireDate] = useState(employee.hireDate ? employee.hireDate.slice(0, 10) : '');

  const mutation = useMutation({
    mutationFn: () => {
      // Build the patch — only include fields that actually changed,
      // and use `null` (not undefined) when the user cleared a field
      // so the server actually nulls it out.
      const patch: UpdateEmployeeRequest = {};
      if (firstName.trim() !== employee.firstName) patch.firstName = firstName.trim();
      if (lastName.trim() !== employee.lastName) patch.lastName = lastName.trim();
      if (jobTitle.trim() !== (employee.jobTitle ?? '')) {
        patch.jobTitle = jobTitle.trim() === '' ? null : jobTitle.trim();
      }
      if ((departmentId || null) !== employee.departmentId) {
        patch.departmentId = departmentId === '' ? null : departmentId;
      }
      if ((managerEmployeeId || null) !== employee.managerEmployeeId) {
        patch.managerEmployeeId = managerEmployeeId === '' ? null : managerEmployeeId;
      }
      const currentHireYmd = employee.hireDate ? employee.hireDate.slice(0, 10) : '';
      if (hireDate !== currentHireYmd) {
        patch.hireDate = hireDate === '' ? null : hireDate;
      }
      return updateEmployee(employee.id, patch);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title={`Edit ${employee.firstName} ${employee.lastName}`} size="md">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="First name"
            name="firstName"
            required
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
          />
          <Input
            label="Last name"
            name="lastName"
            required
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
          />
        </div>

        <Input
          label="Email"
          name="email"
          type="email"
          value={employee.email}
          readOnly
          hint="Email is read-only — change it via a future transfer flow."
        />

        <Input
          label="Job title"
          name="jobTitle"
          placeholder="e.g. Operations Lead"
          value={jobTitle}
          onChange={(e) => {
            setJobTitle(e.target.value);
          }}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">Department</span>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
              }}
              className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Hire date"
            name="hireDate"
            type="date"
            value={hireDate}
            onChange={(e) => {
              setHireDate(e.target.value);
            }}
          />
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Reports to</span>
          <select
            value={managerEmployeeId}
            onChange={(e) => {
              setManagerEmployeeId(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">No manager</option>
            {managerCandidates.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
        </label>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{describeMutationError(mutation.error)}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={!firstName.trim() || !lastName.trim()}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Map invitation action error codes to plain-English toast copy. */
function describeInviteActionError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'invitation_pending') return 'An invitation is already pending for this email.';
    if (code === 'membership_exists') return 'This person is already a member of the org.';
    if (code === 'invitation_not_resendable') return 'This invitation can no longer be resent.';
    if (code === 'role_not_allowed') return 'That role cannot be invited.';
  }
  return err instanceof Error ? err.message : 'Could not send the invitation';
}

/** Map known server error codes to plain-English copy. */
function describeMutationError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'department_not_found') return 'That department was not found in your org.';
    if (code === 'manager_not_found') return 'That manager was not found in your org.';
    if (code === 'cannot_manage_self') return 'An employee can&apos;t be set as their own manager.';
    if (code === 'validation_failed') return 'Some fields look invalid — double-check and retry.';
  }
  return err instanceof Error ? err.message : 'Could not save changes';
}
